import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import FooterUpsell from 'components/FooterUpsell';
import {
  SignUpModalContextProvider,
  useSignUpModalContext
} from '@strava/authentication-ui';
import { trackV2 } from 'utils/analytics';
import { ActivityPublicContextProvider } from './ActivityPublicContext';
import MapElevationChartWrapper from '../MapElevationChartWrapper';
import AthleteBanner from './components/AthleteBanner';
import Comments from './components/Comments';
import Summary from './components/Summary';
import MoreActivities from './components/MoreActivities';
import Photos from './components/Photos';
import Upsell from './components/Upsell';
import SignUpModalWrapper from './components/SignUpModalWrapper';
import { isDev } from '../../../utils/devUtils';

import styles from './styles.scss';

const TIME_DELAY = 6000; // 6 second time delay for variant-b
const HIDE_TIME_DELAY_MODAL = 'hide-time-delay-modal';

const ActivityPublic = ({
  activity,
  photos,
  analyticsData,
  signUpModalProps
}) => {
  const { streams } = activity;
  const { cohort } = signUpModalProps;

  const track = useCallback(
    (fields) => {
      trackV2({ ...analyticsData, ...fields });
    },
    [analyticsData]
  );

  const { openSignUpModal } = useSignUpModalContext();

  useEffect(() => {
    // figure out if the time delay modal has already been shown (and hidden) in this session
    const hideTimeDelayModal = sessionStorage.getItem(HIDE_TIME_DELAY_MODAL);
    if (cohort === 'variant-b' && !hideTimeDelayModal) {
      setTimeout(() => {
        openSignUpModal('generic_time_delayed');

        // don't show the modal again in this session
        sessionStorage.setItem(HIDE_TIME_DELAY_MODAL, true);
      }, TIME_DELAY);
    }
  }, [cohort, openSignUpModal]);

  return (
    <ActivityPublicContextProvider
      showSignUpModal={cohort !== 'control'}
      track={track}
      openSignUpModal={openSignUpModal}
    >
      <main role="main">
        <AthleteBanner {...activity.athlete} />
        <div className="container">
          <div className={`row ${styles.row}`}>
            <div className={`col-md-6 ${styles.mapData}`}>
              {activity.map.present && streams && (
                <MapElevationChartWrapper streams={streams} />
              )}
            </div>
            <div className={`col-md-6 ${styles.activityData}`}>
              <Summary {...activity} />
              <Photos photos={photos} />
              <Comments
                comments={activity.comments}
                totalCommentCount={activity.commentCount}
                activityCommentUrl={activity.authUrls.activityComment}
              />
              <Upsell type={activity.type} />
            </div>
          </div>
        </div>

        <MoreActivities
          activities={activity.relatedActivities}
          athlete={activity.athlete}
        />
        {/* FooterUpsell is shared with the legacy logged-out athlete page,
        so it can't take advantage of the ActivityPublic context */}
        <FooterUpsell
          authUrls={activity.authUrls}
          athlete={activity.athlete}
          analyticsData={analyticsData}
        />

        {signUpModalProps.cohort !== 'control' && (
          <SignUpModalWrapper category="activities" {...signUpModalProps} />
        )}
      </main>
    </ActivityPublicContextProvider>
  );
};

ActivityPublic.defaultProps = {
  photos: []
};

ActivityPublic.propTypes = {
  activity: PropTypes.shape({
    type: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    streams: PropTypes.object.isRequired,
    commentCount: PropTypes.number.isRequired,
    authUrls: PropTypes.object.isRequired,
    map: PropTypes.shape({
      present: PropTypes.any.isRequired,
      src: PropTypes.object.isRequired
    }),
    athlete: PropTypes.shape({}).isRequired,
    relatedActivities: PropTypes.array.isRequired,
    comments: PropTypes.array.isRequired
  }).isRequired,
  photos: PropTypes.arrayOf(PropTypes.object),
  analyticsData: PropTypes.shape().isRequired,
  signUpModalProps: PropTypes.shape({
    cohort: PropTypes.string,
    url: PropTypes.string,
    useMfe: PropTypes.bool
  }).isRequired
};

const ActivityPublicWrapper = ({ useMockData, ...props }) => {
  const [activityPublicProps, setActivityPublicProps] = useState(props);

  // because of the limits of developing locally with activities, can use param `use_mock_data=true`
  // to see a nicely fleshed-out logged-out activity detail page
  useEffect(() => {
    if (useMockData && isDev()) {
      import('./mockData.json')
        .then((module) => {
          const { signUpModalProps } = props;
          setActivityPublicProps({ ...module.default, signUpModalProps });
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.error(err);
        });
    }
    // something about this is causing an infinite loop, so this ensures that we only import the mock data once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <SignUpModalContextProvider
      nameForFollowAthleteVariant={props.activity.athlete.firstName}
    >
      <ActivityPublic {...activityPublicProps} />
    </SignUpModalContextProvider>
  );
};

ActivityPublicWrapper.propTypes = {
  useMockData: PropTypes.bool.isRequired,
  activity: PropTypes.shape({
    athlete: PropTypes.shape({
      firstName: PropTypes.string.isRequired
    }).isRequired
  }).isRequired
};

export default ActivityPublicWrapper;
